import { Checkbox, Input } from "@chakra-ui/react";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";

import {
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormHelperText,
  FormHelperTextIcon,
  FormLabel,
} from "@/design/components/form";
import { ViewTransformationParameter } from "@/features/data-transformation/types";
import { CHILD_PARAMETER } from "@/features/data-transformation/utils/constants.ts";

import { StepFormContext } from "../step-form-context.ts";

interface CheckboxFormProps {
  param: Partial<ViewTransformationParameter>;
  errors?: string;
  hideLabel?: boolean;
  onChange?: (value: boolean) => void;
  id?: string;
  hideErrorIcon?: boolean;
  parentShortName?: string;
}

export const CheckboxForm = ({
  param,
  errors,
  onChange,
  hideLabel,
  id,
  parentShortName,
  hideErrorIcon,
}: CheckboxFormProps) => {
  const { editEnabled, register, setValue } = useContext(StepFormContext)!;

  // Convert string 'true'/'false' to boolean if needed
  const stringToBoolean = (value: any): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true";
    return Boolean(value);
  };

  const defaultValue = stringToBoolean(param.value ?? param.defaultValue ?? false);
  const [isChecked, setIsChecked] = useState<boolean>(defaultValue);

  useEffect(() => {
    setIsChecked(stringToBoolean(param.value ?? param.defaultValue ?? false));
  }, [param]);

  const controlKey = id
    ? `${CHILD_PARAMETER}%%%${parentShortName}%%%${param.shortName}%%%${id}`
    : param.shortName;

  const isEnabled = param.isModifiable && editEnabled;

  const handleChange = (checked: boolean) => {
    setIsChecked(checked);
    setValue(controlKey, checked, {
      shouldDirty: true,
    });
    if (onChange) {
      onChange(checked);
    }
  };

  register(controlKey);

  return (
    <FormControl
      key={controlKey}
      isRequired={param.isMandatory}
      isInvalid={!!errors}
    >
      <div className="flex items-start">
        <Checkbox
          className="mt-0.5 mr-2"
          data-shortname={controlKey}
          id={controlKey}
          isChecked={isChecked}
          isDisabled={!isEnabled}
          onChange={(e) => handleChange(e.target.checked)}
        />
        <Input
          data-shortname={controlKey}
          hidden={true}
          id={`${controlKey}-hidden`}
          required={param.isMandatory}
          type="text"
          value={isChecked.toString()}
          {...register(controlKey)}
          readOnly
        />
        {!hideLabel && (
          <FormLabel 
            htmlFor={controlKey} 
            className="!text-sm !mb-1.5 font-medium leading-none !ml-0"
          >
            {param.name}
          </FormLabel>
        )}
      </div>
      {!errors && param?.hintText && (
        <FormHelperText className="pl-3 text-[13px]">
          <FormHelperTextIcon className="!self-start" />
          {param?.hintText}
        </FormHelperText>
      )}
      <FormErrorMessage
        className={clsx("pl-1 text-[13px]", !hideErrorIcon && "pl-3")}
      >
        {!hideErrorIcon && <FormErrorIcon className="!self-start !mt-0.5" />}
        {errors}
      </FormErrorMessage>
    </FormControl>
  );
}; 